import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { ConfirmationModalService } from '@shared/components/confirmation-modal/confirmation-modal.service';

/**
 * Service that globally intercepts clicks on external links and shows a
 * confirmation modal before allowing navigation.
 *
 * Links that already carry the `appExternalLinkConfirmation` directive are
 * skipped — the directive handles those directly. All other anchor tags
 * pointing to a different http/https origin are intercepted automatically.
 *
 * Registered at boot via `provideAppInitializer`.
 */
@Injectable({ providedIn: 'root' })
export class ExternalLinkInterceptorService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly confirmationModalService = inject(ConfirmationModalService);

  private readonly boundHandler = this.onDocumentClick.bind(this);
  private initialized = false;

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.document.addEventListener('click', this.boundHandler, true);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('click', this.boundHandler, true);
    this.initialized = false;
  }

  private onDocumentClick(event: MouseEvent): void {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
    if (!anchor) return;

    // Skip links already handled by the appExternalLinkConfirmation directive
    if (anchor.hasAttribute('appexternallinkconfirmation')) return;

    const url = anchor.href;
    if (!url) return;

    // Only intercept http/https links that navigate away from the current origin
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
      if (parsed.origin === this.document.location.origin) return;
    } catch {
      // Unparseable href — let the browser handle it
      return;
    }

    event.preventDefault();

    const opensInNewTab = anchor.target === '_blank';
    void this.confirmAndNavigate(url, opensInNewTab);
  }

  /** Extracted for testability (spyOn). */
  navigateSelf(url: string): void {
    this.document.defaultView?.location.assign(url);
  }

  /** Extracted for testability (spyOn). */
  openInNewTab(url: string): void {
    this.document.defaultView?.open(url, '_blank', 'noopener,noreferrer');
  }

  private async confirmAndNavigate(url: string, opensInNewTab: boolean): Promise<void> {
    const confirmed = await this.confirmationModalService.open({
      title: 'Leave Site?',
      subtitle:
        'This link will take you to a page outside of Heidrick Connect.\nConfirm you would like to navigate to the external site?',
      confirmLabel: 'Leave site',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    if (opensInNewTab) {
      this.openInNewTab(url);
    } else {
      this.navigateSelf(url);
    }
  }
}
